import { FC } from 'react';
import { Link } from 'react-router-dom';

import Footer from '../components/footer/Footer';
import Loader from '../components/loader/Loader';
import RunningChart from '../components/chart/RunningChart';
import { useS3RunningStats } from '../hooks/useS3RunningStats';

import './Home.css';

interface HomeProps {
  year?: number;
}

const Home: FC<HomeProps> = ({ year = new Date().getFullYear() }) => {
  const { isLoading, runningStats, error } = useS3RunningStats(`running-stats/${year}/strava_data.json`);

  const isCurrentYear = year === new Date().getFullYear();

  const today = new Date();
  const startOfYear = new Date(new Date().getFullYear(), 0, 1);
  const endOfYear = new Date(today.getFullYear(), 11, 31);
  const oneDay = 1000 * 60 * 60 * 24;

  const todaysDate = new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const todayInDays = Math.ceil((today.getTime() - startOfYear.getTime()) / oneDay);
  const totalDaysInYear = Math.ceil((endOfYear.getTime() - startOfYear.getTime()) / oneDay) + 1;

  const runThisYear = runningStats?.totalDistance || 0;

  const daysLeft = totalDaysInYear - todayInDays;
  const kmLeft = totalDaysInYear - runThisYear;
  const kmBehind = todayInDays - runThisYear;
  const kmLeftPerWeek = kmLeft / (daysLeft / 7);

  let cumulativeDistance = 0;
  const dateOfCompletionStr = runningStats?.activities.find((activity) => {
    cumulativeDistance += activity.distance;
    return cumulativeDistance >= totalDaysInYear;
  })?.start_date;
  const dateOfCompletion = dateOfCompletionStr
    ? new Date(dateOfCompletionStr).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : 'N/A';

  if (isLoading) {
    return (
      <main>
        <Loader />
      </main>
    );
  }

  if (error) {
    return (
      <main>
        <div className="message">Error: {error}</div>
      </main>
    );
  }

  return (
    <main>
      <div className="nav-link">
        <Link to="/">2026</Link>
        <Link to="/2025">2025</Link>
      </div>

      <div className="stats-container">
        {isCurrentYear && (
          <>
            <div className="stats-section">
              <div>Today's date</div>
              <div>{todaysDate}</div>
              <div>Today in days</div>
              <div>{todayInDays}</div>
              <div>Days left</div>
              <div>{daysLeft}</div>
            </div>

            <div className="stats-section">
              <div>Run this year</div>
              <div>{runThisYear.toFixed(1)} km</div>
              <div>Run remaining</div>
              <div>{Math.abs(kmLeft).toFixed(1)}</div>
              <div>km {kmBehind > 0 ? 'behind' : 'ahead 🎉'}</div>
              <div>{Math.abs(kmBehind).toFixed(0)}</div>
              <div>Run remaining per week</div>
              <div>{Math.abs(kmLeftPerWeek).toFixed(1)}</div>
            </div>
          </>
        )}

        {!isCurrentYear && (
          <>
            <div className="stats-section">
              <div>Run this year</div>
              <div>{runThisYear.toFixed(1)} km</div>

              {kmLeft > 0 && (
                <>
                  <div>Run remaining</div>
                  <div>{Math.abs(kmLeft).toFixed(1)}</div>
                  <div>km {kmBehind > 0 ? 'behind' : 'ahead 🎉'}</div>
                  <div className={`stats-value`}>{Math.abs(kmBehind).toFixed(0)}</div>
                  <div>Run remaining per week</div>
                  <div>{Math.abs(kmLeftPerWeek).toFixed(1)}</div>
                </>
              )}

              {kmLeft <= 0 && (
                <>
                  <div className="success-message">🎉 You achieved your goal for {year}! 🎉</div>
                  <div>Date of completion</div>
                  <div>{dateOfCompletion}</div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <RunningChart
        activities={runningStats?.activities ?? []}
        totalDaysInYear={totalDaysInYear}
        isCurrentYear={isCurrentYear}
      />

      <Footer />
    </main>
  );
};

export default Home;
